/**
 * RequeueCell — the revive actions on an ELIMINATED row: "Bring back to queue"
 * (promotion candidate on the next rotation), "To waiting" (parked, never
 * promoted until moved), or "Set to live" (revive AND put it live now).
 *
 * The confirm dialog stays open and BUSY until the write settles. It used to
 * close the instant it was clicked, so the row sat there unchanged while the
 * mutation and the queue refetch ran — several seconds that read as "the button
 * did nothing".
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
  IconBringBack,
  IconMoveToLive,
  IconMoveToWaiting,
  queueActionButton,
  queueActionGlyph,
} from '../queue-action-style';
import type { QueueElement } from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui';
import { cn } from '@/lib/utils';

import { usePromoteFlow } from '../promote/promote-flow';

export type RequeueTarget = 'queue' | 'waiting';
type Action = RequeueTarget | 'live';

export interface RequeueCellProps {
  element: QueueElement;
  onRequeue: (element: QueueElement, to: RequeueTarget) => void | Promise<unknown>;
  pending: boolean;
}

const COPY: Record<Action, { title: string; desc: string; cta: string }> = {
  queue: {
    title: 'Bring back to queue',
    desc: 'returns to the queue as a candidate for the next rotation. Its test history is preserved.',
    cta: 'Bring back',
  },
  waiting: {
    title: 'Bring back to waiting',
    desc: 'moves to Waiting — kept in the bank but never promoted until you move it to the queue. Its test history is preserved.',
    cta: 'To waiting',
  },
  live: {
    title: 'Set to live',
    desc: 'is brought back to the queue and then put live on the tracker straight away. If the pool is at capacity you pick what it replaces next; cancelling there leaves it in the queue.',
    cta: 'Set to live',
  },
};

const GLYPH = 'h-3 w-3 shrink-0';

const RequeueCell: FC<RequeueCellProps> = ({ element, onRequeue, pending }) => {
  const [confirm, setConfirm] = useState<Action | null>(null);
  const [busy, setBusy] = useState(false);
  // Null outside the provider (segments that don't offer a manual promote).
  const promote = usePromoteFlow();
  const disabled = pending || busy;

  const run = async (action: Action) => {
    setBusy(true);
    try {
      // An eliminated row can't be promoted directly — the promote guard
      // refuses a tombstoned element — so revive it first, then hand over to
      // the normal "Move to live now" flow (capacity, victim picker and all).
      await onRequeue(element, action === 'live' ? 'queue' : action);
      if (action === 'live') promote?.request(element);
    } catch {
      // The caller already surfaces the failure as a toast. Swallowing it here
      // is deliberate: `void run(...)` would otherwise leave an unhandled
      // rejection, and a failed revive must not fall through to the promote.
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => setConfirm('queue')}
        disabled={disabled}
        className={queueActionButton('queue')}
      >
        {busy && confirm === null ? (
          <Loader2 className={cn(GLYPH, 'animate-spin')} aria-hidden />
        ) : (
          <IconBringBack className={cn(GLYPH, queueActionGlyph('queue'))} aria-hidden />
        )}
        Bring back to queue
      </button>
      <button
        type="button"
        onClick={() => setConfirm('waiting')}
        disabled={disabled}
        title="Bring back to Waiting — not a promotion candidate until moved to the queue"
        className={queueActionButton('waiting')}
      >
        <IconMoveToWaiting className={cn(GLYPH, queueActionGlyph('waiting'))} aria-hidden />
        To waiting
      </button>
      {promote ? (
        <button
          type="button"
          onClick={() => setConfirm('live')}
          disabled={disabled || Boolean(promote.disabledReason)}
          title={
            promote.disabledReason ??
            'Bring it back and put it live on the tracker now'
          }
          className={queueActionButton('live')}
        >
          <IconMoveToLive className={cn(GLYPH, queueActionGlyph('live'))} aria-hidden />
          Set to live
        </button>
      ) : null}
      <ConfirmDialog
        open={confirm !== null}
        title={confirm ? COPY[confirm].title : ''}
        description={confirm ? `'${element.itemName}' ${COPY[confirm].desc}` : ''}
        confirmLabel={confirm ? COPY[confirm].cta : ''}
        busy={busy}
        onConfirm={() => {
          if (confirm) void run(confirm);
        }}
        onCancel={() => setConfirm(null)}
      />
    </span>
  );
};

export default RequeueCell;
